/**
 * useTaskScope — fonte di verità della pagina Task.
 *
 * Logica di scelta scope iniziale (FE plan sezione 4):
 *  1. query string valida (`?project=12` o `?scope=inbox`);
 *  2. ultimo scope salvato in localStorage (user-specific);
 *  3. primo progetto attivo secondo ordine backend;
 *  4. inbox se non esistono progetti attivi.
 *
 * Quando lo scope cambia:
 *  - aggiorna la query string (replaceState, per non inquinare la history);
 *  - persiste in localStorage.
 *
 * Il hook NON gestisce le fetch (board/summary): quelle vivono in useProjectBoard.
 * Espone `scope` pronto da consumare e `setScope` per cambiarlo.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { TaskScope } from "../../types/tasks";
import {
  parseScopeFromQuery,
  persistScope,
  readPersistedScope,
  scopeToSearchParams,
  scopesEqual,
} from "./scope";

interface UseTaskScopeArgs {
  /**
   * Progetti attivi del tenant. Se la query string e localStorage non
   * specificano uno scope valido, si usa il primo progetto attivo (o inbox).
   */
  activeProjects: Array<{ id: number; is_archived?: boolean }>;
  /** ID utente corrente, per la chiave localStorage user-specific. */
  userId?: number | string | null;
  /**
   * Se true, ignora localStorage/query string e forza uno scope iniziale.
   * Utile per i test o per deep-link programmati.
   */
  initialScope?: TaskScope;
}

interface UseTaskScopeResult {
  scope: TaskScope | null;
  /**
   * Cambia scope. Aggiorna query string e localStorage. Se la pagina deve
   * distinguere "primo avvio" da "cambio utente", è responsabilità del caller
   * leggere `scope === null` solo inizialmente.
   */
  setScope: (next: TaskScope) => void;
}

export function useTaskScope({
  activeProjects,
  userId,
  initialScope,
}: UseTaskScopeArgs): UseTaskScopeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScopeState] = useState<TaskScope | null>(() => {
    if (initialScope) return initialScope;
    // 1) query string
    const fromQuery = parseScopeFromQuery(searchParams);
    if (fromQuery) return fromQuery;
    // 2) localStorage
    const persisted = readPersistedScope(userId);
    if (persisted) return persisted;
    // 3) e 4) vengono risolti nell'effect sotto, quando activeProjects è noto.
    return null;
  });

  // Quando la lista progetti diventa disponibile, completa il fallback.
  useEffect(() => {
    if (scope !== null) return;
    if (initialScope) {
      setScopeState(initialScope);
      return;
    }
    // 3) primo progetto attivo (l'array arriva già ordinato per position dal BE)
    const firstActive = activeProjects.find((p) => !p.is_archived);
    if (firstActive) {
      setScopeState({ kind: "project", projectId: firstActive.id });
    } else if (activeProjects.length === 0) {
      // 4) nessun progetto: vai in inbox solo se proprio non ci sono progetti.
      // Se ci sono solo archiviati, restiamo su null (la UI mostrerà empty state).
      setScopeState({ kind: "inbox" });
    }
    // Se ci sono solo progetti archiviati ma nessuno attivo, scope resta null
    // e la UI deve gestire l'empty state "Nessun progetto attivo".
  }, [activeProjects, scope, initialScope]);

  // Quando scope cambia, aggiorna URL + localStorage.
  useEffect(() => {
    if (scope === null) return;
    const nextParams = scopeToSearchParams(scope, searchParams);
    const currentQs = searchParams.toString();
    const nextQs = nextParams.toString();
    if (currentQs !== nextQs) {
      setSearchParams(nextParams, { replace: true });
    }
    persistScope(scope, userId);
    // searchParams/setSearchParams sono stabili per react-router; non includerli
    // nelle deps evita loop inutili. scopesEqual controlla la sostanza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, userId]);

  const setScope = useCallback((next: TaskScope) => {
    setScopeState((prev) => (scopesEqual(prev, next) ? prev : next));
  }, []);

  return useMemo(() => ({ scope, setScope }), [scope, setScope]);
}
